import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRolesAdminList, type RolesAdminFilter } from '@magimp/shared-hooks';
import {
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  RowCard,
  StatusBadge,
  ToolbarActions,
  useNarrow,
} from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { RoleCreateModal } from './RoleCreateModal.js';
import { RoleEditDrawer } from './RoleEditDrawer.js';

export function RolesListPage() {
  const { t } = useTranslation(['roles', 'common']);
  const narrow = useNarrow();
  const [filter, setFilter] = useState<RolesAdminFilter>('false');
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const q = useRolesAdminList(filter);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>{t('roles:title')}</h1>
        <ToolbarActions>
          <Button kind="ghost" onClick={() => setFilter((f) => (f === 'false' ? 'all' : 'false'))}>
            {filter === 'false' ? t('common:actions.showArchive') : t('common:actions.activeOnly')}
          </Button>
          <Button icon="plus" kind="primary" onClick={() => setCreateOpen(true)}>
            {t('roles:actions.create')}
          </Button>
        </ToolbarActions>
      </header>

      {q.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(q.error)}
          onRetry={() => void q.refetch()}
        />
      )}
      {q.isPending && <LoadingState />}
      {q.isSuccess && q.data.length === 0 && (
        <EmptyState title={t('common:states.emptyTitle')} text={t('roles:empty')} />
      )}
      {q.isSuccess && q.data.length > 0 && !narrow && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--fg-2)', fontSize: 12 }}>
              <th style={{ padding: '8px 6px' }}>{t('roles:columns.name')}</th>
              <th style={{ padding: '8px 6px' }}>{t('roles:columns.identifier')}</th>
              <th style={{ padding: '8px 6px' }}>{t('roles:columns.type')}</th>
              <th style={{ padding: '8px 6px' }}>{t('roles:columns.status')}</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((r) => (
              <tr
                key={r.id}
                style={{ borderTop: '1px solid var(--border-2)', cursor: 'pointer' }}
                onClick={() => setEditId(r.id)}
              >
                <td style={{ padding: '10px 6px' }}>{r.displayName}</td>
                <td
                  style={{
                    padding: '10px 6px',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    color: 'var(--fg-2)',
                  }}
                >
                  {r.name}
                </td>
                <td style={{ padding: '10px 6px' }}>
                  {r.isSystem ? <StatusBadge status="system" /> : '—'}
                </td>
                <td style={{ padding: '10px 6px' }}>
                  <StatusBadge status={r.archived ? 'archived' : 'activeFeminine'} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {q.isSuccess && q.data.length > 0 && narrow && (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {q.data.map((r) => (
            <li key={r.id} style={{ listStyle: 'none' }}>
              <RowCard
                onSelect={() => setEditId(r.id)}
                ariaLabel={r.displayName}
                header={
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' }}>
                    {r.displayName}
                  </div>
                }
                actions={<StatusBadge status={r.archived ? 'archived' : 'activeFeminine'} />}
                fields={[
                  {
                    label: t('roles:columns.identifier'),
                    value: (
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 12,
                          color: 'var(--fg-2)',
                        }}
                      >
                        {r.name}
                      </span>
                    ),
                  },
                  {
                    label: t('roles:columns.type'),
                    value: r.isSystem ? <StatusBadge status="system" /> : '—',
                  },
                ]}
              />
            </li>
          ))}
        </ul>
      )}

      {createOpen && <RoleCreateModal onClose={() => setCreateOpen(false)} />}
      {editId && <RoleEditDrawer id={editId} onClose={() => setEditId(null)} />}
    </div>
  );
}
